import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  Stack,
  Fade,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuth } from "../../../hooks/AuthContext";
import { getSubaccTransactions } from "../../../api/Carrinho/Cripto";

const TransacoesCripto = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transacoes, setTransacoes] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const idOrigem = 3;

  const formatCurrency = (value, currency) => {
    if (!value && value !== 0) return "0";
    const num = parseFloat(value);

    if (currency === "BRL") {
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return num.toLocaleString("pt-BR", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 8,
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Hoje, ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Ontem, ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }

    return (
      date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      " " +
      date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const getTypeLabel = (type) => {
    const typeMap = {
      BUY: "Compra",
      SELL: "Venda",
      "BUY-FEE": "Taxa Compra",
      "SELL-FEE": "Taxa Venda",
    };
    return typeMap[type] || type;
  };

  const getTypeColor = (type) => {
    if (type.includes("BUY")) return "success";
    if (type.includes("SELL")) return "warning";
    return "default";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETE":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "COMPLETE":
        return <CheckCircleIcon fontSize="small" />;
      case "PENDING":
        return <ScheduleIcon fontSize="small" />;
      case "FAILED":
        return <ErrorIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      COMPLETE: "Concluída",
      PENDING: "Pendente",
      FAILED: "Falhou",
    };
    return statusMap[status] || status;
  };

  // Atualize a função mapTransacao para usar idTipoOperacaoCripto
  const mapTransacao = (tx) => {
    // idTipoOperacaoCripto: 1 = Compra, 2 = Venda
    const isCompra = tx.idTipoOperacaoCripto === 1;
    const isVenda = tx.idTipoOperacaoCripto === 2;

    // Determine o tipo baseado no idTipoOperacaoCripto
    let type = "BUY"; // padrão
    if (isCompra) {
      type = "BUY";
    } else if (isVenda) {
      type = "SELL";
    } else {
      // Fallback para o método antigo se idTipoOperacaoCripto não estiver presente
      type = tx.idTipoOperacaoOrigem === 3 ? "BUY" : "SELL";
    }

    return {
      id: tx.id,
      dataCadastro: tx.dataCadastro,
      created: tx.dataCadastro,
      concluded: tx.dataCadastro,
      type: type,
      status: tx.idTipoStatusOperacaoCripto === 1 ? "COMPLETE" : "PENDING",
      currency: tx.moedaCreditoOperacaoCripto?.moeda?.simbolo || "USDT",
      currencyDebito: tx.moedaDebitoOperacaoCripto?.moeda?.simbolo || "BRL",
      amount: tx.valorMoedaCredito,
      amountDebito: tx.valorMoedaDebito,
      price: tx.valorMoedaDebito,
      fee: tx.taxaAplicada,
      balance: tx.valorMoedaCredito,
      transaction_id: tx.id,
      external_id: tx.codigoIntegracao,
      idAgencia: tx.idAgencia,
      idUsuario: tx.idUsuario,
      idCliente: tx.idCliente,
      idTipoOperacaoCripto: tx.idTipoOperacaoCripto, // Adiciona o campo para referência
    };
  };

  const fetchTransacoes = async () => {
    if (!user?.id) {
      setTransacoes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getSubaccTransactions(user.id, idOrigem);

      if (response.errorMessage) {
        throw new Error(response.errorMessage);
      }

      if (Array.isArray(response.data)) {
        const mappedData = response.data.map(mapTransacao);

        const sortedData = mappedData.sort((a, b) => {
          const dateA = new Date(a.dataCadastro);
          const dateB = new Date(b.dataCadastro);
          return dateB - dateA;
        });
        setTransacoes(sortedData);
      } else {
        setTransacoes([]);
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Erro ao buscar transações:", err);
      setError(err.message || "Erro ao carregar transações de cripto");
      setTransacoes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransacoes();
  }, [user?.id]);

  const handleOpenDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTransaction(null);
  };

  const handleRefresh = () => {
    fetchTransacoes();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Fade in timeout={500}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Cabeçalho */}
          <Box
            sx={{
              p: 3,
              bgcolor: "primary.50",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <ReceiptIcon />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="primary.dark"
                  >
                    Histórico de Transações
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Acompanhe todas as suas operações
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {lastUpdate && (
                  <Typography variant="caption" color="text.secondary">
                    Atualizado {formatDateTime(lastUpdate)}
                  </Typography>
                )}
                <Tooltip title="Atualizar histórico">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading}
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "error.light",
                }}
              >
                {error}
              </Alert>
            )}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 6,
                }}
              >
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Carregando transações...
                </Typography>
              </Box>
            )}

            {!loading && transacoes.length === 0 && !error && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  color: "text.secondary",
                }}
              >
                <AccountBalanceWalletIcon
                  sx={{ fontSize: 48, mb: 2, opacity: 0.3 }}
                />
                <Typography variant="body1" gutterBottom>
                  Nenhuma transação encontrada
                </Typography>
                <Typography variant="caption">
                  Suas transações aparecerão aqui
                </Typography>
              </Box>
            )}

            {!loading && transacoes.length > 0 && (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell
                        sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}
                      >
                        Data
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}
                      >
                        Operação
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}
                      >
                        Valor
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}
                      >
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transacoes.map((tx) => (
                      <TableRow
                        key={tx.id}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2">
                            {formatDateTime(tx.created)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: tx.type.includes("BUY")
                                  ? "success.light"
                                  : "warning.light",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: tx.type.includes("BUY")
                                  ? "success.dark"
                                  : "warning.dark",
                              }}
                            >
                              {tx.type.includes("BUY") ? (
                                <TrendingUpIcon fontSize="small" />
                              ) : (
                                <TrendingDownIcon fontSize="small" />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {getTypeLabel(tx.type)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {tx.type.includes("BUY")
                                  ? tx.currency
                                  : tx.currencyDebito}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              sx={{
                                // Ajuste para mostrar positivo para compra e negativo para venda
                                color: tx.type.includes("BUY")
                                  ? "success.main"
                                  : "warning.main",
                              }}
                            >
                              {formatCurrency(
                                Math.abs(tx.amount),
                                tx.type.includes("BUY")
                                  ? tx.currency
                                  : tx.currencyDebito,
                              )}{" "}
                              {tx.type.includes("BUY")
                                ? tx.currency
                                : tx.currencyDebito}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatCurrency(tx.price, "BRL")}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            icon={getStatusIcon(tx.status)}
                            label={getStatusLabel(tx.status)}
                            size="small"
                            sx={{
                              bgcolor: alpha(
                                tx.status === "COMPLETE"
                                  ? theme.palette.success.main
                                  : tx.status === "PENDING"
                                    ? theme.palette.warning.main
                                    : theme.palette.error.main,
                                0.1,
                              ),
                              color:
                                tx.status === "COMPLETE"
                                  ? "success.dark"
                                  : tx.status === "PENDING"
                                    ? "warning.dark"
                                    : "error.dark",
                              border: "none",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Button
                            size="small"
                            onClick={() => handleOpenDetails(tx)}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              borderRadius: 1,
                              fontWeight: 600,
                              color: "primary.main",
                              "&:hover": {
                                bgcolor: "primary.50",
                              },
                            }}
                          >
                            Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && transacoes.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Mostrando {transacoes.length} transações
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Última atualização:{" "}
                  {lastUpdate ? formatDateTime(lastUpdate) : "-"}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Dialog de Detalhes - Atualizado */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.50",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 2.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <ReceiptIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="600">
                Detalhes da Transação
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedTransaction && (
            <Box>
              {/* Cabeçalho com valor */}
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  p: 2.5,
                  borderRadius: 1.5,
                  background: selectedTransaction.type.includes("BUY")
                    ? "linear-gradient(135deg, #10b981 0%, #047857 100%)"
                    : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "white",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -15,
                    right: -15,
                    width: 50,
                    height: 50,
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "50%",
                  },
                }}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    VALOR DA OPERAÇÃO
                  </Typography>
                  <Typography variant="h4" fontWeight="800" sx={{ my: 1 }}>
                    {selectedTransaction.type.includes("BUY") ? "+" : "-"}
                    {formatCurrency(Math.abs(selectedTransaction.amount))}{" "}
                    {selectedTransaction.type.includes("BUY")
                      ? selectedTransaction.currency
                      : selectedTransaction.currencyDebito}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      label={getTypeLabel(selectedTransaction.type)}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      icon={getStatusIcon(selectedTransaction.status)}
                      label={getStatusLabel(selectedTransaction.status)}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    INFORMAÇÕES GERAIS
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    ID da Transação
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {selectedTransaction.id}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Data
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatDateTime(selectedTransaction.created)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    DETALHES DA OPERAÇÃO
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Tipo de Operação
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {selectedTransaction.idTipoOperacaoCripto === 1
                      ? "Compra"
                      : "Venda"}
                    {selectedTransaction.idTipoOperacaoCripto && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        (ID: {selectedTransaction.idTipoOperacaoCripto})
                      </Typography>
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Preço Unitário
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(selectedTransaction.price, "BRL")}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Quantidade
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(Math.abs(selectedTransaction.amount))}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Valor Total
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="primary.main"
                  >
                    {formatCurrency(
                      Math.abs(
                        selectedTransaction.amountDebito *
                          selectedTransaction.amount,
                      ),
                      "BRL",
                    )}
                  </Typography>
                </Grid>

                {selectedTransaction.fee && selectedTransaction.fee > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        TAXAS
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "warning.50",
                          border: "1px solid",
                          borderColor: "warning.100",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="warning.dark"
                        >
                          Taxa:{" "}
                          {formatCurrency(
                            selectedTransaction.fee,
                            selectedTransaction.currency,
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    INFORMAÇÕES ADICIONAIS
                  </Typography>
                </Grid>

                {selectedTransaction.external_id && (
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      ID Externo
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      {selectedTransaction.external_id}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={handleCloseDetails}
            sx={{
              borderRadius: 1,
              fontWeight: 600,
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransacoesCripto;
